import React, { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X, ArrowUp } from "lucide-react";
import logoImg from "@/assets/logo.png";
import logoWhiteImg from "@/assets/logo-white.png";
import { ChatBubble } from "./ChatBubble";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }

      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      {/* NAV spacer */}
      <div className="h-16 md:h-24">
        {/* NAV */}
        <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? "backdrop-blur-lg bg-background/80 border-b border-border/50 shadow-sm" 
            : "bg-transparent border-b border-transparent shadow-none"
        }`}>
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 h-16 md:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)}>
            <img src={logoImg} alt="Aureline Logo" className="h-8 md:h-12 w-auto transition-all" />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-11 text-lg font-semibold text-foreground/70">
            <Link to="/" activeOptions={{ exact: true }} activeProps={{ className: "text-navy-soft" }} className="draw-line-hover hover:text-navy-soft transition">Home</Link>
            <Link to="/about" activeProps={{ className: "text-navy-soft" }} className="draw-line-hover hover:text-navy-soft transition">About</Link>
            <Link to="/services" activeProps={{ className: "text-navy-soft" }} className="draw-line-hover hover:text-navy-soft transition">Services</Link>
            <Link to="/why-aureline" activeProps={{ className: "text-navy-soft" }} className="draw-line-hover hover:text-navy-soft transition">Why Aureline</Link>
            <Link to="/" hash="contact" activeProps={{ className: "text-navy-soft" }} className="draw-line-hover hover:text-navy-soft transition">Contact</Link>
          </nav>
          
          {/* Desktop CTA */}
          <Link to="/" hash="contact" className="hidden md:inline-block">
            <Button className="rounded-full bg-navy text-primary-foreground hover:bg-navy/90 h-12 px-7">
              Request a quote <ArrowRight />
            </Button>
          </Link>

          {/* Mobile Hamburger Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 text-foreground/80 hover:text-navy transition-colors focus:outline-none cursor-pointer"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6 animate-fade-in" /> : <Menu className="w-6 h-6 animate-fade-in" />}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isOpen && (
          <div className="md:hidden border-t border-border/40 bg-background/95 backdrop-blur-xl animate-fade-down duration-200">
            <nav className="flex flex-col px-6 py-6 gap-4 text-base font-semibold text-foreground/85">
              <Link 
                to="/" 
                activeOptions={{ exact: true }} 
                activeProps={{ className: "text-navy-soft" }} 
                className="hover:text-navy-soft transition py-2"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                activeProps={{ className: "text-navy-soft" }} 
                className="hover:text-navy-soft transition py-2"
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/services" 
                activeProps={{ className: "text-navy-soft" }} 
                className="hover:text-navy-soft transition py-2"
                onClick={() => setIsOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/why-aureline" 
                activeProps={{ className: "text-navy-soft" }} 
                className="hover:text-navy-soft transition py-2"
                onClick={() => setIsOpen(false)}
              >
                Why Aureline
              </Link>
              <Link 
                to="/" 
                hash="contact" 
                activeProps={{ className: "text-navy-soft" }} 
                className="hover:text-navy-soft transition py-2"
                onClick={() => setIsOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/" 
                hash="contact" 
                className="mt-2"
                onClick={() => setIsOpen(false)}
              >
                <Button className="w-full rounded-full bg-navy text-primary-foreground hover:bg-navy/90 h-11">
                  Request a quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </header>
    </div>

      {/* CONTENT */}
      <main className="flex-grow">
        {children}
      </main>

      {/* FOOTER */}
      <footer className="bg-navy text-primary-foreground mt-auto">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 py-16 grid md:grid-cols-3 gap-10 xl:gap-16">
          <div>
            <Link to="/" className="flex items-center">
              <img src={logoWhiteImg} alt="Aureline Facility Services" className="h-16 w-auto" />
            </Link>
            <p className="mt-5 text-sm xl:text-base text-primary-foreground/70 max-w-xs xl:max-w-sm">
              Elevated facility services for the spaces people live, work, and gather in.
            </p>
          </div>
          <div>
            <div className="eyebrow text-mint">Explore</div>
            <ul className="mt-4 space-y-2 text-sm text-primary-foreground/80">
              <li><Link to="/" className="hover:text-sky transition">Home</Link></li>
              <li><Link to="/services" className="hover:text-sky transition">Services</Link></li>
              <li><Link to="/about" className="hover:text-sky transition">About</Link></li>
              <li><Link to="/why-aureline" className="hover:text-sky transition">Why Aureline</Link></li>
              <li><Link to="/" hash="contact" className="hover:text-sky transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow text-mint">Contact</div>
            <ul className="mt-4 space-y-2 text-sm text-primary-foreground/80">
              <li><a href="tel:6047614449" className="hover:text-sky transition font-medium">604-761-4449</a></li>
              <li><a href="mailto:info@aurelinefs.com" className="hover:text-sky transition font-medium">info@aurelinefs.com</a></li>
              <li>Unit 608 2818 Main Street, Vancouver BC V5T 0C1</li>
            </ul>
          </div>
        </div>
        <div className="stripe-accent h-2" />
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 py-6 text-xs xl:text-sm text-primary-foreground/55 flex flex-wrap justify-between gap-3">
          <span>© 2026 Aureline Facility Services. All rights reserved.</span>
          <span>Proudly serving communities throughout British Columbia.</span>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-navy text-primary-foreground flex items-center justify-center shadow-elevated transition-all duration-300 hover:bg-navy-soft active:scale-95 cursor-pointer transform ${
          showScrollTop
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-8 pointer-events-none"
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* Chat Assistant Bubble */}
      <ChatBubble />
    </div>
  );
}
